import { MeasureType } from "../types.client";
import { MeasureController } from "../MeasureController";

// 전기 (7개)
import { AcVoltageController } from "./electrical/AcVoltageController";
import { DcVoltageController } from "./electrical/DcVoltageController";
import { GroundResistanceController } from "./electrical/GroundResistanceController";
import { TerminationResistanceController } from "./electrical/TerminationResistanceController";
import { ChargingVoltageController } from "./electrical/ChargingVoltageController";
import { BatteryVoltageController } from "./electrical/BatteryVoltageController";
import { ClockLineVoltageController } from "./electrical/ClockLineVoltageController";

// 통신환경 (5개)
import { LinkSpeedController } from "./communication/LinkSpeedController";
import { OpticalLevelController } from "./communication/OpticalLevelController";
import { TxOutputController } from "./communication/TxOutputController";
import { OutputTerminalLevelController } from "./communication/OutputTerminalLevelController";
import { IoSignalLevelController } from "./communication/IoSignalLevelController";

// 전파환경 (3개)
import { RsrpTerrestrialController } from "./radioWave/RsrpTerrestrialController";
import { RsrpFmRadioController } from "./radioWave/RsrpFmRadioController";
import { RsrpSatelliteController } from "./radioWave/RsrpSatelliteController";

// 무선신호 (2개)
import { EmergencyBellSensitivityController } from "./wireless/EmergencyBellSensitivityController";
import { RemoteMeteringSensitivityController } from "./wireless/RemoteMeteringSensitivityController";

// 전계강도 (2개)
import { CoaxialFieldStrengthController } from "./fieldStrength/CoaxialFieldStrengthController";
import { WirelessFieldStrengthController } from "./fieldStrength/WirelessFieldStrengthController";

// RF카드 (2개)
import { RfMagneticFieldController } from "./rfCard/RfMagneticFieldController";
import { RfRecognitionDistanceController } from "./rfCard/RfRecognitionDistanceController";

// 기타 (3개)
import { TemperatureHumidityController } from "./misc/TemperatureHumidityController";
import { BatterySpecificGravityController } from "./misc/BatterySpecificGravityController";
import { ManholeToxicGasController } from "./misc/ManholeToxicGasController";

// 24개 계측 타입의 서로 다른 제네릭 파라미터를 가진 Controller를
// 단일 Record<MeasureType, ControllerClass>에 저장하기 위해 공통 베이스 타입으로 any 사용
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ControllerClass = new (
  data?: Partial<any>,
  config?: any,
  discussionOverrides?: any
) => MeasureController<any>;

/** 24개 MeasureType → Controller 매핑 */
const CONTROLLER_MAP: Record<MeasureType, ControllerClass> = {
  // 전기 (7개)
  [MeasureType.AC_VOLTAGE]: AcVoltageController,
  [MeasureType.DC_VOLTAGE]: DcVoltageController,
  [MeasureType.GROUND_RESISTANCE]: GroundResistanceController,
  [MeasureType.TERMINATION_RESISTANCE]: TerminationResistanceController,
  [MeasureType.CHARGING_VOLTAGE]: ChargingVoltageController,
  [MeasureType.BATTERY_VOLTAGE]: BatteryVoltageController,
  [MeasureType.CLOCK_LINE_VOLTAGE]: ClockLineVoltageController,
  // 통신환경 (5개)
  [MeasureType.LINK_SPEED]: LinkSpeedController,
  [MeasureType.OPTICAL_LEVEL]: OpticalLevelController,
  [MeasureType.TX_OUTPUT]: TxOutputController,
  [MeasureType.OUTPUT_TERMINAL_LEVEL]: OutputTerminalLevelController,
  [MeasureType.IO_SIGNAL_LEVEL]: IoSignalLevelController,
  // 전파환경 (3개)
  [MeasureType.RSRP_TERRESTRIAL]: RsrpTerrestrialController,
  [MeasureType.RSRP_FM_RADIO]: RsrpFmRadioController,
  [MeasureType.RSRP_SATELLITE]: RsrpSatelliteController,
  // 무선신호 (2개)
  [MeasureType.EMERGENCY_BELL_SENSITIVITY]: EmergencyBellSensitivityController,
  [MeasureType.REMOTE_METERING_SENSITIVITY]: RemoteMeteringSensitivityController,
  // 전계강도 (2개)
  [MeasureType.COAXIAL_FIELD_STRENGTH]: CoaxialFieldStrengthController,
  [MeasureType.WIRELESS_FIELD_STRENGTH]: WirelessFieldStrengthController,
  // RF카드 (2개)
  [MeasureType.RF_MAGNETIC_FIELD]: RfMagneticFieldController,
  [MeasureType.RF_RECOGNITION_DISTANCE]: RfRecognitionDistanceController,
  // 기타 (3개)
  [MeasureType.TEMPERATURE_HUMIDITY]: TemperatureHumidityController,
  [MeasureType.BATTERY_SPECIFIC_GRAVITY]: BatterySpecificGravityController,
  [MeasureType.MANHOLE_TOXIC_GAS]: ManholeToxicGasController,
};

/**
 * MeasureType에 해당하는 Controller 클래스 반환
 * @param type MeasureType enum 값
 * @returns Controller 클래스
 */
export function getMeasureController(type: MeasureType): ControllerClass {
  return CONTROLLER_MAP[type];
}

// 기존 export 유지 (하위 디렉토리 re-export)
// 전기 (7개)
export * from "./electrical";

// 통신환경 (5개)
export * from "./communication";

// 전파환경 (3개)
export * from "./radioWave";

// 무선신호 (2개)
export * from "./wireless";

// 전계강도 (2개)
export * from "./fieldStrength";

// RF카드 (2개)
export * from "./rfCard";

// 기타 (3개)
export * from "./misc";
